import { toBoundingBox, toBounds, unionBounds } from '@/utils/bounds-kit';
import { splitHexAlpha } from '@/utils/color-kit';
import { calculateGradientCoordinate } from '@/utils/file-kit';
import { createSVGElement, XLINK_NS } from '@/utils/svg-kit';
import type { BoundingBox, Bounds } from '@/version-types';
import type { ReadyTextRenderTexture } from '../assets';
import type { TextRenderTexture } from '../types';
import type { RenderContext } from './context';
import { createInstructionNode, wrapTransform, type GlyphItem } from './glyph';

/**
 * 当前一轮填充仲裁结果。
 */
type FillVisual = Readonly<{
    fillId: string | null;
    draw: (
        target: SVGElement,
        layoutBox: BoundingBox,
        paintBounds: Bounds | null,
        glyphs: readonly GlyphItem[],
    ) => void;
}>;

/**
 * 创建 overlay mask 中的黑色背景与白色正文。
 *
 * GlyphItem 携带的本轮 path bounds 会继续传给节点创建，确保纹理/渐变 mask 与正文、描边、geometry
 * 共享同一次轮廓解析；这不会改变 emoji 与 marker 的既有排除规则。
 *
 * @param id - mask 的 renderer 私有 ID。
 * @param box - mask 覆盖的完整绘制区域。
 * @param glyphs - 当前正文的有序字形流。
 * @returns 可供纹理或渐变复用的 SVG mask。
 */
const createGlyphMask = (id: string, box: BoundingBox, glyphs: readonly GlyphItem[]): SVGMaskElement => {
    const mask = createSVGElement('mask', {
        id,
        maskUnits: 'userSpaceOnUse',
        maskContentUnits: 'userSpaceOnUse',
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height,
    });
    const background = createSVGElement('rect', {
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height,
        fill: 'black',
    });
    mask.appendChild(background);
    glyphs.forEach(({ glyph, transform, pathBounds }) => {
        if (glyph.kind === 'marker' || (glyph.kind === 'delegated' && glyph.reason === 'emoji')) return;
        const node = createInstructionNode(glyph, pathBounds);
        if (!node) return;
        node.setAttribute('fill', 'white');
        node.removeAttribute('stroke');
        mask.appendChild(wrapTransform(node, transform));
    });
    return mask;
};

/**
 * 计算花字旋转后 overlay 仍需覆盖的绘制区域。
 *
 * @param layoutBox - 普通文字逻辑区域。
 * @param paintBounds - 花字旋转后的正文视觉边界。
 * @returns 同时覆盖布局和花字正文的区域。
 */
const resolvePaintBox = (layoutBox: BoundingBox, paintBounds: Bounds | null): BoundingBox => {
    if (!paintBounds) return layoutBox;
    const bounds = unionBounds(toBounds(layoutBox), paintBounds);
    return bounds ? toBoundingBox(bounds) : layoutBox;
};

/**
 * 创建 SVG image 节点并同时写入 href 与 xlink:href。
 *
 * @param href - 已加载完成、可序列化的素材地址。
 * @param box - 图片左上位置和尺寸。
 * @param preserveAspectRatio - SVG 图片缩放策略。
 * @returns 已配置的 SVG image。
 */
const createTextureImage = (href: string, box: BoundingBox, preserveAspectRatio: string): SVGImageElement => {
    return createSVGElement(
        'image',
        {
            x: box.left,
            y: box.top,
            width: box.width,
            height: box.height,
            preserveAspectRatio,
            href,
        },
        { [XLINK_NS]: { 'xlink:href': href } },
    );
};

/**
 * 读取当前输入声明并已 ready 的纹理。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param getTexture - 只读取文字纹理的具名 getter。
 * @returns 请求及 ready 位图；不存在完整纹理时返回 null。
 */
const resolveTexture = (
    context: RenderContext,
    getTexture?: (request: TextRenderTexture) => ReadyTextRenderTexture | undefined,
): { request: TextRenderTexture; asset: ReadyTextRenderTexture } | null => {
    const request = context.input.texture;
    if (!request || !getTexture) return null;

    const asset = getTexture(request);
    return asset && asset.naturalWidth > 0 && asset.naturalHeight > 0 ? { request, asset } : null;
};

/**
 * 绘制当前一轮 ready 的纹理 overlay 与字符 marker pattern。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param texture - 已在准备阶段解析的纹理请求与素材。
 * @returns 挂载阶段追加 mask、pattern 与纹理视觉的绘制函数。
 */
const createTextureDraw =
    (context: RenderContext, texture: NonNullable<ReturnType<typeof resolveTexture>>): FillVisual['draw'] =>
    (target, layoutBox, paintBounds, glyphs) => {
        const paintBox = resolvePaintBox(layoutBox, paintBounds);
        const maskId = `${context.namespace}-texture-mask`;
        const fillId = `${context.namespace}-texture`;
        const defs = createSVGElement('defs');
        defs.appendChild(createGlyphMask(maskId, paintBox, glyphs));

        // 无列表时仍保留既有的冗余 marker pattern，避免结构重构改变输出 SVG。
        const repeat = texture.request.stretch === 'repeat';
        const tileWidth = repeat ? texture.asset.naturalWidth : layoutBox.width;
        const tileHeight = repeat ? texture.asset.naturalHeight : layoutBox.height;
        const markerPattern = createSVGElement('pattern', {
            id: fillId,
            'data-text-texture': '',
            patternUnits: 'userSpaceOnUse',
            width: tileWidth,
            height: tileHeight,
        });
        markerPattern.appendChild(
            createTextureImage(
                texture.asset.dataURL,
                { left: 0, top: 0, width: tileWidth, height: tileHeight },
                repeat ? 'none' : 'xMidYMid slice',
            ),
        );
        defs.appendChild(markerPattern);

        let visual: SVGElement;
        if (repeat) {
            visual = createSVGElement('rect', {
                x: paintBox.left,
                y: paintBox.top,
                width: paintBox.width,
                height: paintBox.height,
                fill: `url(#${fillId})`,
            });
        } else {
            visual = createTextureImage(texture.asset.dataURL, paintBox, 'xMidYMid slice');
        }
        visual.setAttribute('data-text-texture-fill', '');
        visual.setAttribute('mask', `url(#${maskId})`);
        target.append(defs, visual);
    };

/**
 * 绘制当前一轮普通文字渐变 mask 与可见填充矩形。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @returns 挂载阶段追加渐变、mask 与填充矩形的绘制函数。
 */
const createGradientDraw =
    (context: RenderContext): FillVisual['draw'] =>
    (target, layoutBox, paintBounds, glyphs) => {
        const gradient = context.input.gradient;
        if (!gradient || gradient.colors.length < 2) return;
        const paintBox = resolvePaintBox(layoutBox, paintBounds);
        const gradientId = `${context.namespace}-gradient`;
        const maskId = `${context.namespace}-gradient-mask`;
        const defs = createSVGElement('defs');
        defs.appendChild(createGlyphMask(maskId, paintBox, glyphs));

        const coordinate = calculateGradientCoordinate(layoutBox.width, layoutBox.height, gradient.angle);
        const gradientNode = createSVGElement('linearGradient', {
            id: gradientId,
            gradientUnits: 'userSpaceOnUse',
            x1: coordinate.x0 + layoutBox.left,
            y1: coordinate.y0 + layoutBox.top,
            x2: coordinate.x1 + layoutBox.left,
            y2: coordinate.y1 + layoutBox.top,
        });
        [...gradient.colors]
            .sort((first, second) => first.value - second.value)
            .forEach((stop) => {
                const color = splitHexAlpha(stop.color);
                const node = createSVGElement('stop', {
                    offset: stop.value > 1 ? `${stop.value}%` : stop.value,
                    'stop-color': color.color,
                    ...(color.opacity < 1 ? { 'stop-opacity': color.opacity } : {}),
                });
                gradientNode.appendChild(node);
            });
        defs.appendChild(gradientNode);
        const rect = createSVGElement('rect', {
            x: paintBox.left,
            y: paintBox.top,
            width: paintBox.width,
            height: paintBox.height,
            fill: `url(#${gradientId})`,
            mask: `url(#${maskId})`,
        });
        target.append(defs, rect);
    };

/**
 * 按 texture → gradient → solid 的既有优先级准备本轮文字填充。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param getTexture - 只读取文字纹理的具名 getter。
 * @returns 填充 ID 与挂载阶段调用的绘制函数。
 */
export const resolveFill = (
    context: RenderContext,
    getTexture?: (request: TextRenderTexture) => ReadyTextRenderTexture | undefined,
): FillVisual => {
    const texture = resolveTexture(context, getTexture);
    if (texture) {
        return {
            fillId: `${context.namespace}-texture`,
            draw: createTextureDraw(context, texture),
        };
    }
    if (context.input.gradient && context.input.gradient.colors.length >= 2) {
        return {
            fillId: `${context.namespace}-gradient`,
            draw: createGradientDraw(context),
        };
    }
    return { fillId: null, draw: () => undefined };
};
